/**
 * Guthaben, Plan, Verbrauch.
 *
 * Alle Zahlen kommen aus `billing/status`, keine einzige aus einer Tabelle.
 * Begründung in `api.ts`: die Verfallsregel des Ledgers steht an genau einer
 * Stelle, und eine zweite Summierung im Browser wäre eine zweite Wahrheit
 * über Geld.
 *
 * WARUM „AKTUALISIEREN" UND KEIN AUTOMATISCHES NACHLADEN: Das Guthaben ändert
 * sich, wenn anderswo ein Lauf läuft — im Connector, im Terminal. Ein Intervall
 * würde die Zahl unter dem Blick des Nutzers ändern, ohne dass er weiß warum.
 * Ein Knopf sagt, wann die Zahl von wann ist.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { connectorPfad, fensterHinweis, freigabeHinweis, MCP_URL } from '../config';
import { claudeCodeBefehl } from '../schluessel';
import { legeSchluesselAn } from '../schluessel-anlegen';
import { ApiFehler, ladeStatus, nochmalSinnvoll, type Status } from '../api';
import { istVerbunden } from '../verbindung';
import { meldeVerbindung } from '../messung';
import { merkeGuthaben } from '../guthaben-kopf';
import { useSitzung } from '../session';
import { bekannteAntworten, fragenVerfuegbar } from './Fragen';
import { ersterLaufVorschlag } from '../erster-lauf';
import PlaeneUndPakete from './PlaeneUndPakete';
import Rechnungsdaten from './Rechnungsdaten';
import Rechnungen from './Rechnungen';
import KontoLoeschen from './KontoLoeschen';
import NewsletterZeile from './NewsletterZeile';
import Widerruf from './Widerruf';
import { guthabenRest } from '../preise';
import { Abschnitt, AppRahmen, ErsterLauf, Balken, Hinweis, Kennzahl, KleinerKnopf, Kopierzeile, Panel, Platzhalter, Seitenkopf, Verweis } from '../ui';
import { locale, t } from '../sprache';

const tag = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString(locale(), { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

export default function Konto() {
  const { session, abmelden } = useSitzung();
  // Mollie leitet nach der Zahlung hierher zurueck. Ohne ein Wort dazu
  // stuende der Kunde vor derselben Seite wie vorher und wuesste nicht, ob
  // seine Zahlung angekommen ist.
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const checkout = params.get('checkout');
  const [status, setStatus] = useState<Status | null>(null);
  const [fehler, setFehler] = useState<string>('');
  const [nochmal, setNochmal] = useState(true);
  const [laedt, setLaedt] = useState(true);
  // Nur im Speicher dieses Tabs, nie im localStorage: siehe schluessel.ts.
  const [frischerSchluessel, setFrischerSchluessel] = useState('');
  const [legtSchluesselAn, setLegtSchluesselAn] = useState(false);
  const [schluesselFehler, setSchluesselFehler] = useState('');

  const holen = useCallback(async () => {
    setLaedt(true);
    setFehler('');
    try {
      const st = await ladeStatus();
      setStatus(st);
      merkeGuthaben(st);
      meldeVerbindung(st);
    } catch (e) {
      const f = e as ApiFehler;
      setFehler(f.message);
      setNochmal(nochmalSinnvoll(f));
      // Abgelaufene Sitzung: abmelden, damit der Riegel zur Anmeldung führt,
      // statt den Nutzer auf einer Seite mit Fehlermeldung sitzen zu lassen.
      if (f.abgemeldet) void abmelden();
    } finally {
      setLaedt(false);
    }
  }, [abmelden]);

  useEffect(() => { void holen(); }, [holen]);
  // /account#verbinden kommt per Router: der Browser springt dann nicht von
  // allein zum Anker, und der Abschnitt steht erst nach dem Laden da.
  // #karte und #pakete kommen aus den Mails zum Guthaben (#155).
  useEffect(() => {
    if (['#verbinden', '#karte', '#pakete', '#rechnungsdaten'].includes(hash) && !laedt) document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash, laedt]);

  async function schluesselAnlegen() {
    if (!status) return;
    setLegtSchluesselAn(true);
    setSchluesselFehler('');
    try {
      const datum = new Date().toLocaleDateString(locale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
      setFrischerSchluessel(await legeSchluesselAn(status.account_id, t(`Claude Code, angelegt ${datum}`, `Claude Code, created ${datum}`)));
    } catch (e) {
      setSchluesselFehler((e as Error).message);
    } finally {
      setLegtSchluesselAn(false);
    }
  }

  const email = session?.user?.email ?? '';
  const g = status?.balance;
  const waehrung = g?.eur_rate_configured ? 'EUR' : 'USD';

  // Deutsche Schreibweise fuer Geld: „0,23 €" statt „0.23 EUR". Die Waehrung
  // bleibt USD, solange kein Kurs hinterlegt ist — dann steht das auch da.
  const betrag = (n: number) =>
    new Intl.NumberFormat(locale(), { style: 'currency', currency: waehrung }).format(Number(n));
  const zahl = (n: number) => new Intl.NumberFormat(locale()).format(n);

  // Verbunden ab dem ersten Aufruf aus Claude (billing/status liefert ihn seit
  // 26.09.2026), vorher und als Rueckfall: ein Lauf diesen Monat. Die
  // Anleitung bleibt erreichbar, steht dann aber nicht mehr vor dem Guthaben.
  const schonVerbunden = istVerbunden(status);

  /*
    WARUM EIN WEG STATT ZWEI LISTEN (Runde 7, 26.09.2026): Neue Konten sahen
    oben „Erster Schritt: verbinden" mit Schritt 1 und 2, darunter „Dein erster
    Lauf" wieder mit 1 und 2. Zwei Nummerierungen fuer einen Ablauf, und bei
    340 px fast drei Bildschirmhoehen vor dem Guthaben. Jetzt ist Verbinden
    Schritt 1 von „Loslegen", die beiden Wege stehen darin nebeneinander.

    WARUM DER SCHLUESSEL-WEG ZUERST STEHT: Er ist der einzige Weg, der Ende zu
    Ende belegt ist (AGENTS.md, „Welcher Zugangsweg heute funktioniert").

    WARUM EIN KNOPF UND KEIN AUTOMATISCH ANGELEGTER SCHLUESSEL: den Klartext
    gibt es genau einmal. Einer, der bei der Registrierung entsteht und den
    niemand gesehen hat, bleibt trotzdem gueltig.
  */
  const wege = (
    // Keine Karten in der Karte: die zwei Wege trennt eine Linie, keine Box.
    <div className="mt-4 grid gap-y-5 border-t border-linie-zart pt-4 lg:grid-cols-2 lg:gap-x-8">
      <div className="min-w-0">
        <p className="m-0 text-[0.9375rem] font-semibold text-tinte">In Claude Code</p>
        {/* Anlass Tobias, 28.09.2026: nicht klar, dass dafuer das Programm fuers
            Terminal noetig ist. Quelle der Angaben: code.claude.com/docs/en/setup
            (Claude Code braucht einen bezahlten Claude-Plan). */}
        <p className="mt-0.5 mb-2 text-[0.8125rem] leading-relaxed text-text-hell">
          {t(<>
            Claude Code ist das Claude-Programm für das Terminal, nicht die Chat-App, und braucht einen bezahlten Claude-Plan.
            Noch nicht installiert? <a href="https://code.claude.com/docs/en/setup" target="_blank" rel="noreferrer" className="text-marke-tief underline decoration-marke/40 underline-offset-[3px] hover:decoration-marke">So installierst du es</a>.
            Ohne Terminal nimm den Connector in claude.ai.
          </>, <>
            Claude Code is the Claude program for the terminal, not the chat app, and needs a paid Claude plan.
            Not installed yet? <a href="https://code.claude.com/docs/en/setup" target="_blank" rel="noreferrer" className="text-marke-tief underline decoration-marke/40 underline-offset-[3px] hover:decoration-marke">How to install it</a>.
            Without a terminal, use the connector in claude.ai.
          </>)}
        </p>
        {!frischerSchluessel ? (
          <>
            <p className="mt-0.5 mb-3 text-[0.8125rem] leading-relaxed text-text-hell">
              {t('Ein Befehl im Terminal. Der Schlüssel für diesen Rechner ist darin schon eingesetzt.', 'One command in the terminal. The key for this computer is already in it.')}
            </p>
            <KleinerKnopf
              type="button"
              onClick={() => void schluesselAnlegen()}
              disabled={legtSchluesselAn || !status}
              className="!border-knopf !bg-knopf !text-auf-marke hover:!bg-knopf-hover"
            >
              {legtSchluesselAn ? t('Wird angelegt …', 'Creating …') : t('Schlüssel und Befehl anlegen', 'Create key and command')}
            </KleinerKnopf>
            {schluesselFehler && <div className="mt-3"><Hinweis art="schlecht">{schluesselFehler}</Hinweis></div>}
          </>
        ) : (
          <>
            <p className="mt-0.5 mb-2.5 text-[0.8125rem] leading-relaxed text-text-hell">{t('Im Terminal ausführen:', 'Run in the terminal:')}</p>
            <Kopierzeile text={claudeCodeBefehl(frischerSchluessel)} />
            <p className="mt-2.5 mb-0 text-[0.8125rem] leading-relaxed text-schlecht">
              {t(<>
                Jetzt kopieren: Der Schlüssel steht nur hier, solange diese Seite offen ist.
                Wir speichern nur seine Prüfsumme. Weg ist nicht schlimm: dann legst du
                unter <Verweis to="/keys">Schlüssel</Verweis> einen neuen an und widerrufst
                diesen.
              </>, <>
                Copy it now: the key is only shown here while this page is open.
                We only store its checksum. Losing it is no problem: create a new one
                under <Verweis to="/keys">API keys</Verweis> and revoke
                this one.
              </>)}
            </p>
          </>
        )}
        <details className="group mt-3 [&_summary::-webkit-details-marker]:hidden">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-[0.8125rem] text-text-hell hover:text-marke-tief">
            <span aria-hidden className="inline-block transition-transform duration-200 group-open:rotate-90">▸</span>
            {t('Ohne Schlüssel', 'Without a key')}
          </summary>
          <div className="mt-2">
            <Kopierzeile text={claudeCodeBefehl()} />
            <p className="mt-2 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">
              {t('Dann meldest du dich beim ersten Aufruf im Browser an.', 'Then you sign in through the browser on the first call.')}
            </p>
          </div>
        </details>
      </div>
      <div className="min-w-0 border-t border-linie-zart pt-5 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-8">
        <p className="m-0 text-[0.9375rem] font-semibold text-tinte">{t('In claude.ai als Connector', 'In claude.ai as a connector')}</p>
        <p className="mt-0.5 mb-3 text-[0.8125rem] leading-relaxed text-text-hell">
          {t(<>Unter <em>{connectorPfad()} → Hinzufügen → Benutzerdefinierten Connector hinzufügen</em> diese Adresse eintragen:</>,
            <>Under <em>{connectorPfad()} → Add → Add custom connector</em>, enter this address:</>)}
        </p>
        <Kopierzeile text={MCP_URL} />
        <p className="mt-2.5 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">
          {t(<>
            Claude leitet dich hierher, du erlaubst den Zugriff. Kein Schlüssel nötig, und
            du kannst den Zugriff jederzeit in Claude wieder beenden.
          </>, <>
            Claude sends you here, you allow access. No key needed, and
            you can end access in Claude at any time.
          </>)}
        </p>
        <p className="mt-2 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">{fensterHinweis()}</p>
        <p className="mt-2 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">{freigabeHinweis()}</p>
      </div>
    </div>
  );

  // Fuer verbundene Konten: dieselben zwei Wege, zugeklappt unter dem Guthaben.
  const verbinden = !laedt && !fehler && (
    <details
      id="verbinden"
      open={hash === '#verbinden'}
      className="group scroll-mt-4 border-t border-linie-zart first:border-t-0 [&_summary::-webkit-details-marker]:hidden"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-karte px-4 py-3.5 hover:bg-grund/50 sm:px-5">
        <span>
          <span className="block text-[0.9375rem] font-semibold text-tinte">{t('Mit Claude verbinden', 'Connect to Claude')}</span>
          <span className="mt-0.5 block text-[0.8125rem] text-text-hell">
            {t('Für einen weiteren Rechner oder ein zweites Claude. Die Arbeit passiert in Claude, nicht hier.', 'For another computer or a second Claude. The work happens in Claude, not here.')}
          </span>
        </span>
        <span className="inline-flex min-h-8 shrink-0 items-center rounded-[0.4375rem] border border-linie bg-flaeche px-3 py-1.5 text-[0.8125rem] font-medium text-text shadow-knopf group-hover:border-linie-stark">
          <span className="group-open:hidden">{t('Anleitung zeigen', 'Show guide')}</span><span className="hidden group-open:inline">{t('Zuklappen', 'Collapse')}</span>
        </span>
      </summary>
      <div className="border-t border-linie-zart px-4 pt-1 pb-5 sm:px-5">
        {wege}
        <p className="mt-4 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">
          {t(<>
            Was du Claude fragen kannst, steht unter <Verweis to="/guide">Anleitung</Verweis>.
            Ein Klick auf einen Run unter <Verweis to="/runs">Runs</Verweis> zeigt, was die Personas gesagt haben.
          </>, <>
            What you can ask Claude is in the <Verweis to="/guide">guide</Verweis>.
            Click a run under <Verweis to="/runs">Runs</Verweis> to see what the personas said.
          </>)}
        </p>
      </div>
    </details>
  );

  return (
    <AppRahmen email={email} abmelden={() => void abmelden()}>
      <Seitenkopf
        titel={t('Konto', 'Account')}
        unter={email ? <>{t('Angemeldet als', 'Signed in as')} <span className="text-text [overflow-wrap:anywhere]">{email}</span></> : undefined}
        aktion={<span className="flex gap-2">
          <KleinerKnopf type="button" onClick={() => void holen()} disabled={laedt}>
            {laedt ? t('Lädt …', 'Loading …') : t('Aktualisieren', 'Refresh')}
          </KleinerKnopf>
          {/* Auf dem Telefon steht Abmelden nur hier, nicht in der Kopfzeile. */}
          <KleinerKnopf type="button" onClick={() => void abmelden()} className="sm:hidden">{t('Abmelden', 'Sign out')}</KleinerKnopf>
        </span>}
      />

      {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}

      {/* Mollie leitet nach jeder Zahlung hierher, auch wenn sie nicht durchging:
          deshalb kein „angekommen", sondern was als Nächstes passiert. */}
      {(checkout === 'zurueck' || checkout === 'success') && (
        <Hinweis art="gut">
          {t(<>
            Danke. Sobald Mollie die Zahlung bestätigt, stehen die Credits unten im Guthaben und die Rechnung kommt per Mail.
            Das dauert meist Sekunden; mit „Aktualisieren" nachladen.
          </>, <>
            Thank you. As soon as Mollie confirms the payment, the credits appear in the balance below and the invoice arrives by email.
            This usually takes seconds. Use “Refresh” to reload.
          </>)}
        </Hinweis>
      )}
      {checkout === 'cancel' && (
        <Hinweis>{t('Der Bezahlvorgang wurde abgebrochen. Es wurde nichts berechnet.', 'The payment was canceled. Nothing was charged.')}</Hinweis>
      )}
      {(params.get('karte') === 'zurueck' || params.get('karte') === 'ok') && (
        <Hinweis art="gut">
          {t(<>
            Danke. Sobald Mollie die Karte bestätigt, kommen die Credits dazu; steht das Guthaben unten
            noch auf dem alten Stand, mit „Aktualisieren" nachladen.
          </>, <>
            Thank you. As soon as Mollie confirms the card, the credits are added. If the balance below
            still shows the old amount, use “Refresh” to reload.
          </>)}
        </Hinweis>
      )}
      {params.get('karte') === 'abbruch' && (
        <Hinweis>{t('Abgebrochen. Es wurde keine Karte gespeichert.', 'Canceled. No card was saved.')}</Hinweis>
      )}

      {g && !g.metered && (
        <Hinweis>{t('Für dieses Konto wird nicht abgerechnet. Runs sind unbegrenzt.', 'This account is not billed. Runs are unlimited.')}</Hinweis>
      )}

      {g?.signup_credits_pending ? (
        <Hinweis>
          {t(<>
            {g.signup_credits_pending} Credits Startguthaben liegen bereit und werden gebucht,
            sobald deine E-Mail-Adresse bestätigt ist.
          </>, <>
            {g.signup_credits_pending} starting credits are ready and will be added
            as soon as your email address is confirmed.
          </>)}
        </Hinweis>
      ) : null}

      {/* Neue Konten sehen zuerst, was als Naechstes zu tun ist; das Guthaben
          von 350 Credits beantwortet diese Frage nicht. Ein Ablauf, eine
          Nummerierung: Verbinden ist Schritt 1 (siehe oben). Anlass fuer den
          Satz „antwortet Claude allein“ (Tester, 05.11.2026): Konto angelegt,
          nie verbunden, in Claude gefragt und nicht erkannt, dass 99takes
          nicht mitgearbeitet hat. */}
      {!laedt && !fehler && !schonVerbunden && (() => {
        const antworten = bekannteAntworten(session?.user?.id ?? session?.user?.email);
        return (
          <div className="mb-12">
            <Abschnitt
              id="verbinden"
              titel={t('Loslegen', 'Get started')}
              unter={t('Zwei Schritte bis zum ersten Run. Bis du verbunden bist, antwortet Claude allein, ohne 99takes. Verbinden kostet nichts, den Preis eines Runs nennt Claude vorher.', 'Two steps to your first run. Until you connect, Claude answers on its own, without 99takes. Connecting costs nothing, Claude names the price of a run beforehand.')}
            >
              <ErsterLauf
                verbinden={wege}
                vorschlag={ersterLaufVorschlag(antworten)}
                passend={!!antworten && Object.keys(antworten).length > 0}
              />
            </Abschnitt>
          </div>
        );
      })()}

      <Abschnitt titel={t('Guthaben', 'Balance')}>
        {!status && laedt && (
          <Panel className="grid sm:grid-cols-2">
            <div className="p-4 sm:p-5"><Platzhalter zeilen={3} /></div>
            <div className="border-t border-linie-zart p-4 sm:border-t-0 sm:border-l sm:p-5"><Platzhalter zeilen={3} /></div>
          </Panel>
        )}
        {!status && !laedt && fehler && (
          <Panel className="px-4 py-5 sm:px-5">
            <p className="m-0 text-[0.9375rem] leading-relaxed text-text-leise">
              {nochmal
                ? t('Das Guthaben ließ sich nicht laden. Mit „Aktualisieren“ noch einmal versuchen.', 'The balance could not be loaded. Use “Refresh” to try again.')
                : t('Das Guthaben erscheint hier, sobald wir die Konten verbunden haben.', 'The balance appears here as soon as we have linked the accounts.')}
            </p>
          </Panel>
        )}
        {g && (
          <>
            <Panel>
              <div className="grid sm:grid-cols-[1.1fr_1fr]">
                <div className="p-4 sm:p-5">
                  <Kennzahl
                    gross
                    label={t('Verfügbar', 'Available')}
                    wert={g.metered ? <>{zahl(g.balance ?? 0)} <span className="text-[0.9375rem] font-sans font-semibold tracking-normal text-text-leise">{t('Credits', 'credits')}</span></> : t('unbegrenzt', 'unlimited')}
                    zusatz={g.metered && (g.plan_allowance_remaining || g.pack_credits) ? (
                      <>
                        {g.plan_allowance_remaining ? <>{t(
                          `${zahl(g.plan_allowance_remaining)} aus dem Monatskontingent, verfällt am ${tag(g.plan_allowance_expires_at)}.`,
                          `${zahl(g.plan_allowance_remaining)} from the monthly allowance, expires on ${tag(g.plan_allowance_expires_at)}.`,
                        )} </> : null}
                        {guthabenRest(g, zahl, tag)}
                      </>
                    ) : undefined}
                  />
                </div>
                <div className="border-t border-linie-zart p-4 sm:border-t-0 sm:border-l sm:p-5">
                  <Kennzahl
                    label={t(`Verbraucht seit ${tag(g.month.from)}`, `Used since ${tag(g.month.from)}`)}
                    wert={<>
                      {zahl(g.month.credits_used)}{' '}
                      <span className="text-[0.875rem] font-sans font-semibold tracking-normal text-text-leise">
                        {g.month.credit_cap != null ? t(`von ${zahl(g.month.credit_cap)} Credits`, `of ${zahl(g.month.credit_cap)} credits`) : t('Credits', 'credits')}
                      </span>
                    </>}
                  />
                  {g.month.credit_cap != null && (
                    <div className="mt-3">
                      <Balken wert={g.month.credits_used} von={g.month.credit_cap} label={t('Verbrauch gegen die Monatsgrenze', 'Usage against the monthly limit')} />
                      <div className="mt-1.5 text-[0.8125rem] text-text-hell tabular-nums">{t(`Monatsgrenze ${zahl(g.month.credit_cap)} Credits`, `Monthly limit ${zahl(g.month.credit_cap)} credits`)}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-b-karte border-t border-linie-zart bg-grund/60 px-4 py-3 text-[0.8125rem] text-text-hell tabular-nums sm:px-5">
                <span>
                  <span className="font-medium text-text">{g.month.runs}</span> {t(g.month.runs === 1 ? 'Run' : 'Runs', g.month.runs === 1 ? 'run' : 'runs')} {t('diesen Monat', 'this month')}
                </span>
                <span>
                  {t('Modellkosten', 'Model costs')} <span className="font-medium text-text">{betrag(g.month.eur_used)}</span>
                  {g.month.eur_cap != null ? <> {t('von höchstens', 'of at most')} {betrag(g.month.eur_cap)}</> : null}
                </span>
              </div>
            </Panel>
          </>
        )}
      </Abschnitt>

      {/* Plan waehlen, Credits nachkaufen, was ein Credit kauft (27.09.2026). */}
      {status && (
        <div className="mt-12">
          {status.testmodus && (
            <Hinweis>{t('Testmodus: Zahlungen laufen über Mollie-Testkarten, es wird kein Geld bewegt.', 'Test mode: payments use Mollie test cards, no money is moved.')}</Hinweis>
          )}
          {/* Fehlt bei einer billing-Function von vor Mollie: dann gibt es das Feld nicht. */}
          {'rechnungsdaten' in status && <Rechnungsdaten status={status} gespeichert={() => void holen()} />}
          <PlaeneUndPakete status={status} eurInUsd={!g?.eur_rate_configured} neuLaden={() => void holen()} />
          {/* § 356a BGB (#272): nur, solange eine Bestellung widerrufbar ist. */}
          <Widerruf status={status} email={session?.user?.email ?? ''} neuLaden={() => void holen()} />
          <Rechnungen neu={status} />
        </div>
      )}

      {/* Zugang (03.10.2026): Verbinden fuer verbundene Konten und die
          Schluessel, die seitdem keinen eigenen Reiter mehr haben. Beides
          braucht man selten, deshalb unten und zugeklappt. */}
      {!laedt && !fehler && (
        <Abschnitt titel={t('Zugang', 'Access')}>
          <Panel>
            {schonVerbunden && verbinden}
            {status?.newsletter && (
              <NewsletterZeile stand={status.newsletter} geaendert={(n) => setStatus((s) => (s ? { ...s, newsletter: n } : s))} />
            )}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 border-t border-linie-zart px-4 py-3.5 first:border-t-0 sm:px-5">
              <span className="min-w-0">
                <span className="block text-[0.9375rem] font-semibold text-tinte">{t('API-Schlüssel', 'API keys')}</span>
                <span className="mt-0.5 block text-[0.8125rem] text-text-hell">{t('Für Server, Skripte und Build-Schritte ohne Anmeldung im Browser.', 'For servers, scripts and build steps without a browser sign-in.')}</span>
              </span>
              <Link
                to="/keys"
                className="inline-flex min-h-8 items-center rounded-[0.4375rem] border border-linie bg-flaeche px-3 py-1.5 text-[0.8125rem] font-medium text-text no-underline shadow-knopf hover:border-linie-stark hover:bg-ebene hover:text-tinte"
              >
                {t('Schlüssel verwalten', 'Manage keys')}
              </Link>
            </div>
            {/* Die Fragen nach der Registrierung: eine Bitte, keine Aufgabe. */}
            {fragenVerfuegbar(session?.user?.id ?? session?.user?.email) && (
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 border-t border-linie-zart px-4 py-3.5 sm:px-5">
                <span className="min-w-0">
                  <span className="block text-[0.9375rem] font-semibold text-tinte">{t('Deine Angaben', 'Your answers')}</span>
                  <span className="mt-0.5 block text-[0.8125rem] text-text-hell">{t('Fünf kurze Fragen zu dir und deinen Texten. Daraus kommen die Vorschläge für deinen ersten Run.', 'Five short questions about you and your copy. The suggestions for your first run come from them.')}</span>
                </span>
                <Link
                  to="/welcome?edit=1&next=%2Faccount"
                  className="inline-flex min-h-8 items-center rounded-[0.4375rem] border border-linie bg-flaeche px-3 py-1.5 text-[0.8125rem] font-medium text-text no-underline shadow-knopf hover:border-linie-stark hover:bg-ebene hover:text-tinte"
                >
                  {t('Ändern', 'Edit')}
                </Link>
              </div>
            )}
          </Panel>
        </Abschnitt>
      )}

      {/* Ganz unten und zugeklappt (#274, AGB § 12). */}
      {!laedt && !fehler && session?.user?.email && (
        <div className="mt-6">
          <KontoLoeschen email={session.user.email} abo={!!status?.subscription && status.subscription.status !== 'canceled'} newsletter={status?.newsletter} />
        </div>
      )}

    </AppRahmen>
  );
}
